import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { ArrowLeft, TrendingUp, Award, Lightbulb } from "lucide-react";
import HealthOverview from "./HealthOverview";
import HealthScores from "./HealthScores";
import OverallPerformanceGauge from "./components/OverallPerformanceGauge";
import {
  calculateCompositionScore,
  calculateWHR,
  getLastNDaysRecords,
  calculateTrend,
  extractMetricValues,
} from "./utils/healthCalculations";

const HealthDashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("overview");
  const [overallScore, setOverallScore] = useState(0);

  // Calculate overall performance score
  useEffect(() => {
    if (user?.dailyRecords?.records) {
      const last30Days = getLastNDaysRecords(user.dailyRecords.records, 30);
      if (last30Days.length > 0) {
        const latestRecord = last30Days[last30Days.length - 1];

        // Calculate composition score (0-100)
        const whr = calculateWHR(latestRecord.waistLine, latestRecord.hip);
        const compositionScore = parseFloat(
          calculateCompositionScore(
            latestRecord.fat,
            latestRecord.muscles,
            latestRecord.bmi,
            whr
          )
        );

        // Calculate trend scores (0-100)
        const weightValues = extractMetricValues(last30Days, "weight");
        const fatValues = extractMetricValues(last30Days, "fat");
        const muscleValues = extractMetricValues(last30Days, "muscles");

        const weightTrend = calculateTrend(weightValues);
        const fatTrend = calculateTrend(fatValues);
        const muscleTrend = calculateTrend(muscleValues);

        // Trend scoring: down weight = good, down fat = good, up muscle = good
        let trendScore = 50; // Base score
        if (weightTrend === "down") trendScore += 15;
        if (fatTrend === "down") trendScore += 15;
        if (muscleTrend === "up") trendScore += 20;

        // Visceral fat score (0-100)
        const visceralFat = latestRecord.visceralFat || 0;
        let visceralScore = 100;
        if (visceralFat >= 15) visceralScore = 30;
        else if (visceralFat >= 10) visceralScore = 60;

        // Overall score: weighted average
        const overall = Math.round(
          compositionScore * 0.4 + trendScore * 0.3 + visceralScore * 0.3
        );

        setOverallScore(Math.min(100, Math.max(0, overall)));
      }
    }
  }, [user]);

  const tabs = [
    { id: "overview", label: "30-Day Trends", icon: TrendingUp },
    { id: "scores", label: "Health Scores", icon: Award },
    // { id: "insights", label: "AI Insights", icon: Lightbulb }, // Coming soon
  ];

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="w-full max-w-6xl mx-auto bg-white shadow-sm">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
          <div className="px-5 py-4">
            <div className="flex items-center gap-3 mb-4">
              <button
                onClick={() => navigate("/profile")}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-700" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Health Analytics
                </h1>
                <p className="text-sm text-gray-500">
                  Smart insights from your health data
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab.id
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Overall Performance Gauge - Always visible at top */}
        <div className="p-5 border-b border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="max-w-md mx-auto">
            <OverallPerformanceGauge score={overallScore} />
          </div>
        </div>

        {/* Content */}
        <div>
          {activeTab === "overview" && <HealthOverview />}
          {activeTab === "scores" && <HealthScores />}
        </div>
      </div>
    </div>
  );
};

export default HealthDashboard;
